import React, { useRef, useState, useEffect } from 'react';
import { Play, StopCircle, RotateCcw } from 'lucide-react';

const Hero = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  const [isTiny, setIsTiny] = useState(typeof window !== 'undefined' ? window.innerWidth < 430 : false);
  const BUBBLE_SIZE = isMobile ? 64 : 80; // tamaño reducido en mobile
  const [positions, setPositions] = useState<Array<{ x: number; y: number }>>([]);
  const [isBouncing, setIsBouncing] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [logoOrigin, setLogoOrigin] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [overlayHidden, setOverlayHidden] = useState(false);
  const draggingIndexRef = useRef<number | null>(null);
  const offsetRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const velocitiesRef = useRef<Array<{ vx: number; vy: number }>>([]);
  const rafRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number | null>(null);

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTiny(window.innerWidth < 430);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const computeGridPositions = (width: number, height: number) => {
    const GRID_WIDTH = 320;
    const GRID_HEIGHT = 320;
    const colXs = [20, 120, 220];
    const rowYs = [20, 120, 220];
    const offsetX = Math.max(0, isMobile ? (width - GRID_WIDTH) / 2 : (width - GRID_WIDTH) * 0.75);
    const extraTop = isMobile ? 0 : 20;
    const offsetY = Math.max(0, (height - GRID_HEIGHT) / 2 + extraTop);
    const clamp = (val: number, min: number, max: number) => Math.max(min, Math.min(val, max));
    const result: Array<{ x: number; y: number }> = [];
    for (let r = 0; r < rowYs.length; r++) {
      for (let c = 0; c < colXs.length; c++) {
        const x = clamp(colXs[c] + offsetX, 0, width - BUBBLE_SIZE);
        const y = clamp(rowYs[r] + offsetY, 0, height - BUBBLE_SIZE);
        result.push({ x, y });
      }
    }
    return result;
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const updatePositions = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      const GRID_WIDTH = 320;
      const GRID_HEIGHT = 320;
      const offsetX = Math.max(0, (w - GRID_WIDTH) / 2);
      const extraTop = isMobile ? 0 : 20;
      const offsetY = Math.max(0, (h - GRID_HEIGHT) / 2 + extraTop);
      setLogoOrigin({ x: offsetX, y: offsetY });
      setPositions(computeGridPositions(w, h));
    };
    updatePositions();
    const ro = new ResizeObserver(() => updatePositions());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setHasScrolled(true);
      if (isPaused) return;
      setIsBouncing(true);
    };
    window.addEventListener('scroll', onScroll as any, { passive: true } as any);
    return () => {
      window.removeEventListener('scroll', onScroll as any);
    };
  }, [isPaused]);

  useEffect(() => {
    const randVel = () => {
      const min = 60;
      const max = 120;
      const speed = min + Math.random() * (max - min);
      const angle = Math.random() * Math.PI * 2;
      return { vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed };
    };
    const startBounce = () => {
      velocitiesRef.current = positions.map(() => randVel());
      lastTimeRef.current = performance.now();
      const MARGIN = 5;
      const step = (t: number) => {
        const el = containerRef.current;
        if (!el) return;
        const w = el.clientWidth;
        const h = el.clientHeight;
        const last = lastTimeRef.current ?? t;
        const dt = (t - last) / 1000;
        lastTimeRef.current = t;
        setPositions((prev) => {
          const next = [...prev];
          for (let i = 0; i < next.length; i++) {
            if (draggingIndexRef.current === i) continue;
            const v = velocitiesRef.current[i] || { vx: 0, vy: 0 };
            let x = next[i].x + v.vx * dt;
            let y = next[i].y + v.vy * dt;
            if (x <= MARGIN) {
              x = MARGIN;
              v.vx = Math.abs(v.vx);
            } else if (x >= w - BUBBLE_SIZE - MARGIN) {
              x = w - BUBBLE_SIZE - MARGIN;
              v.vx = -Math.abs(v.vx);
            }
            if (y <= MARGIN) {
              y = MARGIN;
              v.vy = Math.abs(v.vy);
            } else if (y >= h - BUBBLE_SIZE - MARGIN) {
              y = h - BUBBLE_SIZE - MARGIN;
              v.vy = -Math.abs(v.vy);
            }
            next[i] = { x, y };
            velocitiesRef.current[i] = v;
          }
          return next;
        });
        rafRef.current = requestAnimationFrame(step);
      };
      rafRef.current = requestAnimationFrame(step);
    };
    const stopBounce = () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastTimeRef.current = null;
    };
    if (isBouncing) {
      startBounce();
    } else {
      stopBounce();
    }
    return () => {
      stopBounce();
    };
  }, [isBouncing, positions.length]);

  const onPointerDown = (i: number) => (e: React.PointerEvent<HTMLDivElement>) => {
    if (isTiny) return;
    draggingIndexRef.current = i;
    const rect = (e.target as HTMLDivElement).getBoundingClientRect();
    offsetRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
    (e.target as HTMLDivElement).setPointerCapture(e.pointerId);
    velocitiesRef.current[i] = { vx: 0, vy: 0 };
    setIsDragging(true);
    setOverlayHidden(true);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (draggingIndexRef.current == null || !containerRef.current) return;
    const i = draggingIndexRef.current;
    const containerRect = containerRef.current.getBoundingClientRect();
    const newX = Math.max(0, Math.min(e.clientX - containerRect.left - offsetRef.current.x, containerRect.width - BUBBLE_SIZE));
    const newY = Math.max(0, Math.min(e.clientY - containerRect.top - offsetRef.current.y, containerRect.height - BUBBLE_SIZE));
    setPositions((prev) => {
      const next = [...prev];
      next[i] = { x: newX, y: newY };
      return next;
    });
  };

  const endDrag = () => {
    const idx = draggingIndexRef.current;
    draggingIndexRef.current = null;
    setIsDragging(false);
    if (isBouncing && idx != null) {
      const min = 60;
      const max = 120;
      const speed = min + Math.random() * (max - min);
      const angle = Math.random() * Math.PI * 2;
      velocitiesRef.current[idx] = { vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed };
    }
  };

  const togglePause = () => {
    if (!isPaused) {
      setIsPaused(true);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      setIsBouncing(false);
    } else {
      setIsPaused(false);
      setIsBouncing(true);
    }
  };

  const restartAnimation = () => {
    setIsPaused(false);
    setHasScrolled(false);
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    const el = containerRef.current;
    if (el) {
      const w = el.clientWidth;
      const h = el.clientHeight;
      setPositions(computeGridPositions(w, h));
    }
    velocitiesRef.current = positions.map(() => ({ vx: 0, vy: 0 }));
    setIsBouncing(false);
    setOverlayHidden(false);
  };

  return (
    <div className="relative w-full h-[500px] md:h-[380px] mt-10 md:mt-20 md:pt-16 dark:bg-slate-950">
      <div className="absolute inset-0 hero-bg dark:hidden"></div>
      <svg style={{ position: 'absolute', width: 0, height: 0 }} aria-hidden="true" focusable="false">
        <defs>
          <filter id="goo">
            <feGaussianBlur in="SourceGraphic" stdDeviation="12" result="blur" />
            <feColorMatrix in="blur" mode="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 20 -10" result="goo" />
            <feComposite in="SourceGraphic" in2="goo" operator="atop" />
          </filter>
        </defs>
      </svg>

      {!isBouncing && !isPaused && !overlayHidden && positions.length > 0 && (
        <img
          src="/assets/unionesdesenfocadas512x512.png"
          alt="Hero Background"
          width={338}
          height={338}
          fetchPriority="high"
          className="absolute pointer-events-none"
          style={{ left: (positions[0]?.x ?? logoOrigin.x) - 31, top: (positions[0]?.y ?? logoOrigin.y) - 33, width: 338, height: 338, opacity: 1, zIndex: 0 }}
        />
      )}
      <div
        ref={containerRef}
        className={`absolute inset-0 goo w-full h-full z-10 ${isTiny ? 'touch-auto' : 'touch-none'}`}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
      >
        {positions.map((pos, i) => {
          const isLight = [0, 2, 4, 6, 8].includes(i);
          return (
            <div
              key={i}
              className={`bubble ${isLight ? 'bubble-light' : 'bubble-dark'} absolute draggable ${isDragging && draggingIndexRef.current === i ? 'dragging' : ''}`}
              style={{ left: pos.x, top: pos.y }}
              onPointerDown={onPointerDown(i)}
            />
          );
        })}
      </div>

      {hasScrolled && (
        <div className="absolute bottom-4 left-4 z-20 flex items-center gap-3">
          <button
            type="button"
            onClick={togglePause}
            className="flex items-center gap-2 rounded-md bg-white/90 px-3 py-2 shadow hover:bg-white dark:bg-slate-800 dark:text-white dark:hover:bg-slate-700 pointer-events-auto"
          >
            {isPaused ? <Play size={16} /> : <StopCircle size={16} />}
            {!isPaused ? 'Stop' : 'Play'}
          </button>
          <button
            type="button"
            onClick={restartAnimation}
            className="flex items-center gap-2 rounded-md bg-white/90 px-3 py-2 shadow hover:bg-white dark:bg-slate-800 dark:text-white dark:hover:bg-slate-700 pointer-events-auto"
          >
            <RotateCcw size={16} />
            Reset
          </button>
        </div>
      )}

      <div className="relative z-10 max-w-7xl mx-auto px-4 h-full flex items-center justify-center md:justify-start pointer-events-none">
        <div className="hidden md:flex w-full md:w-1/2 flex-col items-center md:items-start justify-center text-center md:text-left gap-6 md:pl-20">
          <h1 className="text-4xl md:text-3xl font-extrabold leading-tight text-gray-900 dark:text-white">Movete libremente</h1>
          <p className="text-lg md:text-base font-normal leading-snug text-gray-600 dark:text-gray-300">Hosting flexible, seguro y escalable</p>
        </div>
      </div>
    </div>
  );
};

export default Hero;
